import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Settings2, Columns as ColumnsIcon, Palette, Puzzle } from "lucide-react";
import RichTextEditor from "@/components/cms/RichTextEditor";
import UploadButton from "./UploadButton";
import { LEAF_LABELS, WIDGET_OPTIONS } from "./constants";

const h = "h-9";

function LeafFields({ node, set }) {
  const d = node.data || {};
  switch (node.type) {
    case "heading":
      return (
        <div className="space-y-2">
          <Input className={h} value={d.text || ""} onChange={(e) => set({ text: e.target.value })} placeholder="Texte du titre" data-testid="prop-heading-text" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={d.level || "2"} onValueChange={(v) => set({ level: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="1">Titre principal (H1)</SelectItem><SelectItem value="2">Grand titre</SelectItem><SelectItem value="3">Sous-titre</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "richtext":
      return <RichTextEditor value={d.html} onChange={(html) => set({ html })} />;
    case "image":
      return (
        <div className="space-y-2">
          {d.url ? <img src={d.url} alt="" className="h-24 w-full rounded-lg border border-slate-200 object-cover" /> : null}
          <div className="flex flex-wrap gap-2 items-center">
            <UploadButton onUploaded={(url) => set({ url })} testId="prop-image-upload" />
          </div>
          <Input className={h} value={d.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="…ou coller une URL" data-testid="prop-image-url" />
          <Input className={h} value={d.alt || ""} onChange={(e) => set({ alt: e.target.value })} placeholder="Texte alternatif (ALT — SEO/accessibilité)" data-testid="prop-image-alt" />
          <Input className={h} value={d.caption || ""} onChange={(e) => set({ caption: e.target.value })} placeholder="Légende (facultatif)" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={d.width || "full"} onValueChange={(v) => set({ width: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="small">Petite</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="full">Pleine largeur</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "center"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {(d.images || []).map((im, i) => (
              <div key={i} className="relative group">
                <img src={im.url} alt="" className="w-full h-16 object-cover rounded-lg border border-slate-200" />
                <button type="button" onClick={() => set({ images: d.images.filter((_, j) => j !== i) })}
                  className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5" data-testid={`prop-gallery-remove-${i}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
          <UploadButton multiple label="Ajouter des images" onUploaded={(url) => set((cur) => ({ images: [...((cur || {}).images || []), { url, alt: "" }] }))} testId="prop-gallery-upload" />
          <Select value={d.columns || "3"} onValueChange={(v) => set({ columns: v })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="2">2 colonnes</SelectItem><SelectItem value="3">3 colonnes</SelectItem><SelectItem value="4">4 colonnes</SelectItem></SelectContent>
          </Select>
        </div>
      );
    case "button":
      return (
        <div className="space-y-2">
          <Input className={h} value={d.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Libellé" data-testid="prop-button-label" />
          <Input className={h} value={d.href || ""} onChange={(e) => set({ href: e.target.value })} placeholder="Lien (/ma-page ou https://…)" data-testid="prop-button-href" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={d.style || "primary"} onValueChange={(v) => set({ style: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="primary">Plein</SelectItem><SelectItem value="outline">Contour</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "video":
      return <Input className={h} value={d.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="Lien YouTube ou Vimeo" data-testid="prop-video-url" />;
    case "widget":
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs text-slate-500"><Puzzle size={13} /> Bloc métier (contenu dynamique protégé)</div>
          <Select value={d.widget || "devis_cta"} onValueChange={(v) => set({ widget: v })}>
            <SelectTrigger className="h-9" data-testid="prop-widget-select"><SelectValue /></SelectTrigger>
            <SelectContent>{WIDGET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400">Le contenu (offres, partenaires, événements…) se met à jour automatiquement. Vous le placez, le reste est géré par le site.</p>
        </div>
      );
    case "spacer":
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm">Hauteur</Label>
          <Input type="number" className="w-24 h-9" value={d.size || 32} onChange={(e) => set({ size: Number(e.target.value) })} />
          <span className="text-sm text-slate-400">px</span>
        </div>
      );
    default:
      return <p className="text-sm text-slate-400">Aucun réglage pour ce bloc.</p>;
  }
}

function SectionFields({ section, setData, setColumns }) {
  const d = section.data || {};
  const nbCols = (section.columns || []).length;
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Nombre de colonnes</Label>
        <Select value={String(nbCols)} onValueChange={(v) => setColumns(Number(v))}>
          <SelectTrigger className="h-9" data-testid="prop-section-columns"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 colonne</SelectItem>
            <SelectItem value="2">2 colonnes</SelectItem>
            <SelectItem value="3">3 colonnes</SelectItem>
            <SelectItem value="4">4 colonnes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Couleur de fond</Label>
        <div className="flex items-center gap-2">
          <input type="color" value={d.bg || "#ffffff"} onChange={(e) => setData({ bg: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid="prop-section-bg" />
          <Input className="h-9 flex-1" value={d.bg || ""} onChange={(e) => setData({ bg: e.target.value })} placeholder="transparent" />
          {d.bg && <button onClick={() => setData({ bg: "" })} className="text-xs text-slate-400 hover:text-slate-600">Retirer</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Espacement vertical</Label>
          <Select value={d.paddingY || "md"} onValueChange={(v) => setData({ paddingY: v })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem><SelectItem value="sm">Petit</SelectItem>
              <SelectItem value="md">Moyen</SelectItem><SelectItem value="lg">Grand</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Largeur</Label>
          <Select value={d.maxWidth || "normal"} onValueChange={(v) => setData({ maxWidth: v })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="narrow">Étroite</SelectItem><SelectItem value="normal">Normale</SelectItem>
              <SelectItem value="wide">Large</SelectItem><SelectItem value="full">Pleine</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
        <div><div className="text-sm font-medium text-slate-700">Position libre</div><div className="text-[11px] text-slate-400">Déplacer/redimensionner librement — empilé sur mobile</div></div>
        <Switch checked={!!d.free} onCheckedChange={(v) => setData({ free: v })} data-testid="prop-section-free" />
      </div>
      {d.free && (
        <div><Label className="text-xs">Hauteur de la zone (px)</Label>
          <Input type="number" className="h-9" value={d.freeHeight || 420} onChange={(e) => setData({ freeHeight: Number(e.target.value) })} data-testid="prop-section-freeheight" /></div>
      )}
    </div>
  );
}

function StyleFields({ data, onChange, isSection }) {
  const d = data || {};
  const sel = (label, key, opts) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={d[key] || opts[0].v} onValueChange={(v) => onChange({ [key]: v })}>
        <SelectTrigger className="h-9" data-testid={`prop-style-${key}`}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  const space = [{ v: "none", l: "Aucun" }, { v: "sm", l: "Petit" }, { v: "md", l: "Moyen" }, { v: "lg", l: "Grand" }];
  return (
    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid="prop-style">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Palette size={13} /> Style</div>
      <div className="grid grid-cols-2 gap-2">
        {sel("Marge haut", "mt", space)}
        {sel("Marge bas", "mb", space)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {sel("Arrondi", "radius", [{ v: "none", l: "Aucun" }, { v: "sm", l: "Léger" }, { v: "md", l: "Moyen" }, { v: "lg", l: "Fort" }, { v: "full", l: "Rond" }])}
        {sel("Ombre", "shadow", [{ v: "none", l: "Aucune" }, { v: "sm", l: "Légère" }, { v: "md", l: "Moyenne" }, { v: "lg", l: "Forte" }])}
      </div>
      {sel("Animation d'entrée", "anim", [{ v: "none", l: "Aucune" }, { v: "fade", l: "Fondu" }, { v: "up", l: "Glisser vers le haut" }])}
      {!isSection && (
        <div>
          <Label className="text-xs">Couleur du texte</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={d.textColor || "#334155"} onChange={(e) => onChange({ textColor: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid="prop-style-textcolor" />
            {d.textColor && <button onClick={() => onChange({ textColor: "" })} className="text-xs text-slate-400 hover:text-slate-600">Retirer</button>}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
        <div className="text-sm font-medium text-slate-700">Masquer sur mobile</div>
        <Switch checked={!!d.hideMobile} onCheckedChange={(v) => onChange({ hideMobile: v })} data-testid="prop-style-hidemobile" />
      </div>
    </div>
  );
}

export default function PropertiesPanel({ selected, updateLeafData, updateSectionData, setSectionColumns }) {
  if (!selected) {
    return (
      <div className="text-center text-sm text-slate-400 py-10 px-4" data-testid="prop-empty">
        <Settings2 size={28} className="mx-auto mb-2 opacity-40" />
        Sélectionnez une section ou un bloc dans la page pour en modifier les propriétés.
      </div>
    );
  }
  const isSection = selected.type === "section";
  const onStyle = isSection ? updateSectionData : updateLeafData;
  return (
    <div data-testid="properties-panel">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">
        {isSection ? <ColumnsIcon size={14} /> : <Settings2 size={14} />}
        {isSection ? "Section" : (LEAF_LABELS[selected.type] || selected.type)}
      </div>
      {isSection
        ? <SectionFields section={selected} setData={updateSectionData} setColumns={setSectionColumns} />
        : <LeafFields node={selected} set={updateLeafData} />}
      <StyleFields data={selected.data} onChange={onStyle} isSection={isSection} />
    </div>
  );
}
